import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Search, Shield, ChevronDown, ChevronUp, Loader2, Check, X } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';

interface Practice {
  id: string;
  name: string;
  is_active: boolean;
}

interface Workspace {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  seat_count: number;
  bypass_paywall: boolean;
  subscription_status: string | null;
  practice_count: number;
  practices: Practice[];
  created_at: string;
}

const WorkspaceAdmin: React.FC = () => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [editingSeats, setEditingSeats] = useState<Record<string, number>>({});
  const [savingOrg, setSavingOrg] = useState<string | null>(null);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/workspaces`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch workspaces');
      const data = await res.json();
      setWorkspaces(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const updateWorkspace = async (orgId: string, updates: { seat_count?: number; bypass_paywall?: boolean }) => {
    setSavingOrg(orgId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/v1/admin/workspaces/${orgId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Update failed');
      }
      const updated = await res.json();
      setWorkspaces(prev => prev.map(w => w.id === orgId ? updated : w));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingOrg(null);
    }
  };

  const handleToggleBypass = (org: Workspace) => {
    updateWorkspace(org.id, { bypass_paywall: !org.bypass_paywall });
  };

  const handleSeatChange = (orgId: string, value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 1) {
      setEditingSeats(prev => ({ ...prev, [orgId]: num }));
    }
  };

  const handleSeatSave = (orgId: string) => {
    const newCount = editingSeats[orgId];
    if (newCount !== undefined) {
      updateWorkspace(orgId, { seat_count: newCount });
      setEditingSeats(prev => {
        const next = { ...prev };
        delete next[orgId];
        return next;
      });
    }
  };

  const filteredWorkspaces = workspaces.filter(w =>
    w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Loading workspaces...</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-50 rounded-lg">
          <Shield className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workspace Admin</h1>
          <p className="text-sm text-gray-500">Manage organization seats and access</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search workspaces..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
        />
      </div>

      <div className="text-xs text-gray-400 mb-3 px-1">
        {filteredWorkspaces.length} workspace{filteredWorkspaces.length !== 1 ? 's' : ''}
      </div>

      <div className="space-y-3">
        {filteredWorkspaces.map(org => {
          const isExpanded = expandedOrg === org.id;
          const editingSeatVal = editingSeats[org.id];
          const isSaving = savingOrg === org.id;

          return (
            <div key={org.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-4 flex items-center gap-4">
                <div className="p-2 bg-gray-50 rounded-lg">
                  <Building2 className="w-5 h-5 text-gray-600" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 truncate">{org.name}</span>
                    <span className="text-xs text-gray-400">({org.slug})</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>{org.practice_count} practice{org.practice_count !== 1 ? 's' : ''}</span>
                    <span className="text-gray-300">|</span>
                    <span>{org.seat_count} seat{org.seat_count !== 1 ? 's' : ''}</span>
                    {org.subscription_status && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          org.subscription_status === 'active' ? 'bg-green-50 text-green-700' :
                          org.subscription_status === 'trialing' ? 'bg-blue-50 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{org.subscription_status}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Bypass toggle */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Active</span>
                  <button
                    onClick={() => handleToggleBypass(org)}
                    disabled={isSaving}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      org.bypass_paywall ? 'bg-blue-600' : 'bg-gray-300'
                    } ${isSaving ? 'opacity-50' : ''}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      org.bypass_paywall ? 'translate-x-5' : ''
                    }`} />
                  </button>
                </div>

                {/* Seat count editor */}
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={1}
                    value={editingSeatVal !== undefined ? editingSeatVal : org.seat_count}
                    onChange={e => handleSeatChange(org.id, e.target.value)}
                    className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {editingSeatVal !== undefined && editingSeatVal !== org.seat_count && (
                    <button
                      onClick={() => handleSeatSave(org.id)}
                      disabled={isSaving}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </button>
                  )}
                </div>

                <button
                  onClick={() => setExpandedOrg(isExpanded ? null : org.id)}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded"
                >
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>

              {isExpanded && org.practices.length > 0 && (
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                  <div className="text-xs font-medium text-gray-500 mb-2">Practices</div>
                  <div className="space-y-1.5">
                    {org.practices.map(p => (
                      <div key={p.id} className="flex items-center gap-2 text-sm">
                        <span className={`w-2 h-2 rounded-full ${p.is_active ? 'bg-green-400' : 'bg-gray-300'}`} />
                        <span className="text-gray-700">{p.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filteredWorkspaces.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            {searchQuery ? 'No workspaces match your search.' : 'No workspaces found.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspaceAdmin;
